import { useEffect, useState } from 'react'
import { z } from 'zod/v3';
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import type { RouterOutputs } from '@/types/trpc'
import { trpc } from '@/lib/trpc'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { Input } from '@deepguide-ai/ui/input'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from '@deepguide-ai/ui/input-group'
import { Label } from '@deepguide-ai/ui/label'
import { Textarea } from '@deepguide-ai/ui/textarea'
import { ProfileTypeCombobox } from './ProfileTypeCombobox'
import { BrowserProfileDialog } from './BrowserDaialog'
// import { useSessionUrlCapture } from './useSessionUrlCapture'

const createProfileSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(100, 'Name must be 100 characters or less'),
  profileType: z.string().optional(),
  email: z.string().email('Invalid email address').optional().or(z.literal('')),
  description: z.string().optional(),
  loginUrl: z
    .string()
    .min(1, 'Login URL is required')
    .refine(
      (val) => {
        try {
          new URL(`https://${val}`)
          return true
        } catch {
          return false
        }
      },
      { message: 'Must be a valid URL (e.g., app.example.com/login)' }
    ),
})

type CreateProfileFormData = z.infer<typeof createProfileSchema>

interface CreateBrowserProfileDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: (profile: any, sessionViewerUrl?: string) => void
}

export function CreateBrowserProfileDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreateBrowserProfileDialogProps) {
  const [sessionUrl, setSessionUrl] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [sessionReleased, setSessionReleased] = useState(false)
  const [sessionLoading, setSessionLoading] = useState(false)
  const [createdProfile, setCreatedProfile] = useState<any | null>(null)
  // Removed: URL capture polling is no longer needed
  // const {
  //   captureSessionUrl,
  //   isCapturing: isCapturingSessionUrl,
  //   isRequestingCapture,
  //   isPollingCapture,
  // } = useSessionUrlCapture(createdProfile?.id ?? null)

  const form = useForm<CreateProfileFormData>({
    resolver: zodResolver(createProfileSchema),
    defaultValues: {
      name: '',
      profileType: '',
      email: '',
      description: '',
      loginUrl: '',
    },
  })

  const createMutation = useMutation({
    ...trpc.browserProfile.create.mutationOptions(),
    onSuccess: (data) => {
      setCreatedProfile(data.profile)
      setSessionLoading(false)
      setSessionId(data.profile?.steelSessionId ?? null)
      setSessionUrl(data.sessionViewerUrl || data.profile?.steelSessionUrl || null)
      setViewerOpen(true)
      setSessionReleased(false)
      form.reset()
    },
    onError: (error) => {
      setSessionLoading(false)
      toast.error(`Failed to create execution identity: ${error.message}`)
    },
  })

  const startSessionMutation = useMutation({
    ...trpc.browserProfile.startSession.mutationOptions(),
    onSuccess: (data) => {
      setSessionLoading(false)
      setSessionId(data.sessionId)
      setSessionUrl(data.sessionViewerUrl)
      setSessionReleased(false)
      setCreatedProfile(data.profile)
    },
    onError: (error: any) => {
      setSessionLoading(false)
      setSessionUrl(null)
      setSessionId(null)
      toast.error(`Failed to restart session: ${error.message || 'Unknown error'}`)
    },
  })

  const handleSubmit = form.handleSubmit((data) => {
    setSessionLoading(true)
    createMutation.mutate({
      name: data.name,
      profileType: data.profileType || undefined,
      email: data.email || undefined,
      description: data.description || undefined,
      loginUrl: `https://${data.loginUrl}`,
      useSteelBrowser: true, // Always use Steel for compliance profiles
    })
  })

  const handleLoginCompletionChoice = async (hasLoginInfo: boolean, sessionIdFromDialog: string | null) => {
    if (!createdProfile?.id) {
      console.log("no profile, skipping save session")
      return
    }

    try {
      // Use setLoginCompletion which properly handles both session saving and login status
      await setLoginCompletionMutation.mutateAsync({
        profileId: createdProfile.id,
        hasLoginInfo,
        sessionId: sessionIdFromDialog || undefined,
      })
      // Success handled by mutation's onSuccess callback
    } catch (error) {
      // Error already handled by mutation's onError callback
      console.error('Failed to save session:', error)
      throw error // Re-throw so dialog stays open
    }
  }

  const handleDiscardSession = async () => {
    if (
      !createdProfile?.id ||
      sessionReleased ||
      discardSessionMutation.isPending
    ) {
      return
    }
    try {
      await discardSessionMutation.mutateAsync({ profileId: createdProfile.id })
      setSessionReleased(true)
    } catch (error) {
      // Error already handled by mutation's onError callback
      console.error('Failed to discard session:', error)
    }
  }

  const handleLogInAgain = async () => {
    if (!createdProfile) return

    await handleDiscardSession()
    setSessionLoading(true)
    setSessionUrl(null)
    setSessionId(null)
    setSessionReleased(false)

    startSessionMutation.mutate({
      id: createdProfile.id,
    })
  }

  const setLoginCompletionMutation = useMutation({
    ...trpc.browserProfile.setLoginCompletion.mutationOptions(),
    onSuccess: (result) => {
      setSessionReleased(true)
      const updatedProfile = result.profile
      if (updatedProfile) {
        toast.success('Session saved. Profile created successfully.')
        setViewerOpen(false)
        setSessionUrl(null)
        setSessionId(null)
        onSuccess?.(updatedProfile, updatedProfile?.steelSessionUrl)
        setCreatedProfile(null)
      }
    },
    onError: (error) => {
      toast.error(`Failed to save session: ${error.message}`)
    },
  })

  const discardSessionMutation = useMutation({
    ...trpc.browserProfile.discardSession.mutationOptions(),
    onError: (error) => {
      toast.error(`Failed to close session: ${error.message}`)
    },
  })

  const handleViewerChange = async (value: boolean) => {
    setViewerOpen(value)
    if (!value && createdProfile?.id && !sessionReleased) {
      await handleDiscardSession()
    }
  }

  const handleClose = () => {
    if (
      !createMutation.isPending &&
      !sessionLoading
    ) {
      if (createdProfile?.id && !sessionReleased) {
        void handleDiscardSession()
      }
      form.reset()
      setSessionUrl(null)
      setSessionId(null)
      setViewerOpen(false)
      setSessionReleased(false)
      setSessionLoading(false)
      setCreatedProfile(null)
      discardSessionMutation.reset()
      setLoginCompletionMutation.reset()
      onOpenChange(false)
    }
  }

  useEffect(() => {
    if (!open) {
      if (createdProfile?.id && !sessionReleased) {
        void handleDiscardSession()
      }
      setSessionUrl(null)
      setSessionId(null)
      setViewerOpen(false)
      setSessionReleased(false)
      setSessionLoading(false)
      setCreatedProfile(null)
      discardSessionMutation.reset()
      setLoginCompletionMutation.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Release session on unmount if still active
  useEffect(() => {
    return () => {
      if (createdProfile?.id && !sessionReleased) {
        void handleDiscardSession()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdProfile?.id, sessionReleased])

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className='max-h-[90vh] gap-0 overflow-y-auto p-0'>
          <DialogHeader>
            <DialogTitle>Create Execution Identity</DialogTitle>
            <DialogDescription>
              Create an identity for workflow execution. You'll be able to sign in manually.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className='space-y-4 px-6 py-4'>
            <div className='space-y-1.5'>
              <Label htmlFor='name'>
                Identity Name <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='name'
                placeholder='e.g., Stripe Admin, AWS Manager'
                {...form.register('name')}
              />
              {form.formState.errors.name && (
                <p className='text-xs text-destructive'>
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>

            <div className='space-y-1.5'>
              <Label htmlFor='profileType'>Role</Label>
              <ProfileTypeCombobox
                value={form.watch('profileType')}
                onChange={(value) => form.setValue('profileType', value)}
                placeholder='Select or enter role...'
              />
              <p className='text-[11px] leading-4 text-text-subtle'>
                Role (e.g., Administrator, Manager, Auditor)
              </p>
            </div>

            <div className='space-y-1.5'>
              <Label htmlFor='email'>Email</Label>
              <Input
                id='email'
                type='email'
                placeholder='user@example.com'
                {...form.register('email')}
              />
              {form.formState.errors.email && (
                <p className='text-xs text-destructive'>
                  {form.formState.errors.email.message}
                </p>
              )}
              <p className='text-[11px] leading-4 text-text-subtle'>
                Optional - for tracking which account is used
              </p>
            </div>

            <div className='space-y-1.5'>
              <Label htmlFor='description'>Description</Label>
              <Textarea
                id='description'
                placeholder='Optional notes about this identity'
                rows={3}
                {...form.register('description')}
              />
            </div>

            <div className='space-y-1.5'>
              <Label htmlFor='loginUrl'>
                Initial URL <span className='text-destructive'>*</span>
              </Label>
              <InputGroup>
                <InputGroupAddon>
                  <InputGroupText>https://</InputGroupText>
                </InputGroupAddon>
                <InputGroupInput
                  id='loginUrl'
                  placeholder='app.example.com'
                  className='!pl-1'
                  {...form.register('loginUrl')}
                />
              </InputGroup>
              {form.formState.errors.loginUrl && (
                <p className='text-xs text-destructive'>
                  {form.formState.errors.loginUrl.message}
                </p>
              )}
              <p className='text-[11px] leading-4 text-text-subtle'>
                URL where you'll open the application
              </p>
            </div>
          </form>

          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={handleClose}
              disabled={
                createMutation.isPending ||
                sessionLoading
              }
            >
              Cancel
            </Button>
            <Button
              type='submit'
              onClick={handleSubmit}
              disabled={
                createMutation.isPending ||
                sessionLoading
              }
            >
              {createMutation.isPending ||
              sessionLoading ? (
                <>
                  <Loader2 className='h-4 w-4 animate-spin' />
                  Creating...
                </>
              ) : (
                'Create Identity'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

{  viewerOpen&& <BrowserProfileDialog
        open={viewerOpen}
        onOpenChange={handleViewerChange}
        profile={createdProfile}
        sessionUrl={sessionUrl}
        sessionLoading={sessionLoading}
        sessionId={sessionId}
        onLogInAgain={handleLogInAgain}
        isLoggingInAgain={createMutation.isPending || startSessionMutation.isPending}
        onReleaseSession={handleDiscardSession}
        onLoginCompletionChoice={handleLoginCompletionChoice}
        isSettingLoginCompletion={setLoginCompletionMutation.isPending}
      />}
    </>
  )
}
